import 'regenerator-runtime/runtime';
import 'url-search-params-polyfill';
import * as Sentry from '@sentry/browser';
import React from 'react';
import axios from 'axios';
import ReactDOM from 'react-dom';
import { wixAxiosConfig } from '@wix/wix-axios-config';
import { ExperimentsProvider } from '@wix/wix-experiments-react';
import { i18nInboxCommonService } from '@wix/inbox-common';
import { I18nextProvider } from 'react-i18next';
import { Provider } from 'react-redux';
import i18n from './i18n';
import wix from './services/wix';
import configureStore from './store/configureStore';
import serverApi from './services/serverApi';
import { fedopsLogger, LoadingPhase } from './services/fedopsLogger';
import { loadingScheduler } from './services/loading-scheduler';
import { extractSiteData } from './services/site-data';
import { siteDataFromSiteJson } from './services/site-json';
import { loadChat } from './chat-loader';

const staticsBaseUrl = window.__STATICS_BASE_URL__;
const {
  publicVisibility,
  editorVisibility,
  chatOnboarded,
  businessChatEnabled,
  socialChatEnabled,
} = window.__SETTINGS__;
const experiments = window.__EXPERIMENTS__;
const chatInitResult = window.__CHAT_INIT_RESULT__;
const isLcfMandatory =
  window.__IS_LCF_MANDATORY__ === undefined
    ? { isMandatory: false, isContact: false }
    : window.__IS_LCF_MANDATORY__;
const siteJson = window.__SITE_JSON__;
const widgetMode = window.__WIDGET_MODE__;
const unpkgUrl = window.__UNPKG_URL__;
const locale = wix.getLocale();
const viewMode = wix.getViewMode();
const deviceType = wix.getDeviceType();
const visibilitySettings = {
  publicVisibility,
  editorVisibility,
  chatOnboarded,
};

// extract client version from statics-base-url
const urlParts = staticsBaseUrl
  .split('/')
  .filter(Boolean)
  .reverse();
const clientVersion = urlParts.length > 1 ? urlParts[0] : undefined;
if (clientVersion) {
  Sentry.onLoad(() => {
    Sentry.init({
      enabled: !!clientVersion,
      release: `chat-widget@${clientVersion}`,
    });
  });
}

fedopsLogger.init({ appName: 'chat-widget' });
i18nInboxCommonService.i18n({ locale });
wixAxiosConfig(axios, { baseURL: '/' });
const i18nData = i18n({ locale, baseUrl: staticsBaseUrl });

const siteData = siteDataFromSiteJson(siteJson);
const initialDataFromSiteData = extractSiteData(siteData);

const store = configureStore({
  i18nData,
  visibilitySettings,
  viewMode,
  locale,
  experiments,
  deviceType,
  serverApi,
  chatInitResult,
  businessChatEnabled,
  socialChatEnabled,
  isLcfMandatory,
  widgetMode,
  ...initialDataFromSiteData,
});

fedopsLogger.appLoadingPhaseStart(LoadingPhase.Bootstrap);

export const init = (component: React.ReactElement<any>) => {
  void loadingScheduler.onReadyToPaint().then(() => {
    fedopsLogger.appLoadingPhaseStart(LoadingPhase.Paint);

    ReactDOM.render(
      <Provider store={store}>
        <I18nextProvider i18n={i18nData}>
          <ExperimentsProvider options={{ experiments }}>
            {component}
          </ExperimentsProvider>
        </I18nextProvider>
      </Provider>,
      document.getElementById('root'),
    );

    loadChat({ widgetMode, experiments, unpkgUrl });
  });

  loadingScheduler.triggerPaint();
};
